import type React from "react";

import { NumericBadge } from "@/design-system/NumericBadge";
import { isDefined } from "@/helpers/util";
import { ToggleGroup as ArkToggleGroup } from "@/ui-adapters/ArkToggleGroup";

export type TagSelectItem<TValue extends string = string> = {
  label: string;
  value: TValue;
  count?: number;
  "data-testid"?: string;
  isDisabled?: boolean;
};

export type TagSelectProps<TValue extends string = string> = {
  items: TagSelectItem<TValue>[];
  values: TValue[];
  isMultiple?: boolean;
  onChange: (value: TValue[]) => void;
  "aria-labelledby"?: string;
};

export function TagSelect<TValue extends string = string>({
  values,
  items,
  isMultiple,
  onChange,
  ...props
}: TagSelectProps<TValue>): React.ReactNode {
  return (
    <ArkToggleGroup.Root
      className="flex flex-wrap items-center gap-space-sm"
      value={values}
      onValueChange={(details) => onChange(details.value as TValue[])}
      multiple={isMultiple}
      {...props}
    >
      {items.map((item) => (
        <ArkToggleGroup.Item
          className="flex button-press cursor-pointer items-center gap-space-xs rounded-full border border-selection-border bg-transparent px-space-md py-space-sm text-center text-body text-text-primary transition-colors disabled:cursor-not-allowed disabled:border-transparent disabled:bg-selection-disabled-surface disabled:text-text-on-disabled data-[state=off]:hover:border-selection-border-hover data-[state=on]:border-selection-border-hover data-[state=on]:bg-selection-selected-surface data-[state=on]:text-selection-selected"
          key={item.value}
          value={item.value}
          data-testid={item["data-testid"]}
          disabled={item.isDisabled}
        >
          <span>{item.label}</span>
          {isDefined(item.count) && item.count > 0 && <NumericBadge variant="filled" count={item.count} />}
        </ArkToggleGroup.Item>
      ))}
    </ArkToggleGroup.Root>
  );
}
